import { useState } from "react";
import { Loader2, Volume2, Users, Plug, ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateUserStats } from "@/lib/queryKeys";
import { FocusMascotScale } from "@/components/FocusMascotScale";
import { WifiQualityScale } from "@/components/WifiQualityScale";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mascot } from "@/components/Mascot";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface PendingReflection {
  id: string;
  cafe_id: string;
  cafe_name: string;
  checked_in_at: string;
}

interface PostVisitReflectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingReflection: PendingReflection | null;
  onComplete: () => void;
}

// Outlet options
const outletOptions = [
  { value: 1, label: "None" },
  { value: 2, label: "Limited" },
  { value: 3, label: "Some" },
  { value: 5, label: "Plenty" },
];

// Crowdedness options
const crowdednessOptions = [
  { value: 1, label: "Empty" },
  { value: 2, label: "Quiet" },
  { value: 3, label: "Moderate" },
  { value: 4, label: "Busy" },
  { value: 5, label: "Packed" },
];

// Noise options
const noiseOptions = [
  { value: "quiet", label: "Quiet" },
  { value: "moderate", label: "Moderate" },
  { value: "lively", label: "Lively" },
  { value: "loud", label: "Loud" },
] as const;

export function PostVisitReflectionDialog({
  open,
  onOpenChange,
  pendingReflection,
  onComplete,
}: PostVisitReflectionDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [focusRating, setFocusRating] = useState<number | null>(null);
  const [wifiQuality, setWifiQuality] = useState<number | null>(null);
  const [outletsAvailable, setOutletsAvailable] = useState<number | null>(null);
  const [crowdedness, setCrowdedness] = useState<number | null>(null);
  const [noiseLevel, setNoiseLevel] = useState<"quiet" | "moderate" | "lively" | "loud" | null>(null);
  const [wouldWorkAgain, setWouldWorkAgain] = useState<boolean | null>(null);
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setFocusRating(null);
    setWifiQuality(null);
    setOutletsAvailable(null);
    setCrowdedness(null);
    setNoiseLevel(null);
    setWouldWorkAgain(null);
    setNotes("");
  };

  const handleSubmit = async () => {
    if (!pendingReflection) return;
    
    setIsSubmitting(true);
    
    const { error } = await supabase
      .from("check_ins")
      .update({
        focus_rating: focusRating,
        wifi_speed: wifiQuality,
        outlets_available: outletsAvailable,
        crowdedness,
        noise_level: noiseLevel,
        would_work_here_again: wouldWorkAgain,
        notes: notes.trim() || null,
        reflection_completed: true,
      })
      .eq("id", pendingReflection.id);

    setIsSubmitting(false);

    if (error) {
      console.error("Error saving reflection:", error);
      toast({
        title: "Error saving reflection",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Thanks for sharing!",
      description: "Your feedback helps other remote workers find great spots.",
    });

    // Invalidate queries to refresh data
    if (user?.id) invalidateUserStats(queryClient, user.id);
    // Retained: not covered by invalidateUserStats.
    queryClient.invalidateQueries({ queryKey: ["cafe-checkins", pendingReflection.cafe_id] });
    queryClient.invalidateQueries({ queryKey: ["recent-cafe-conditions"] });
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ["work-journal", user.id] });
    }
    
    resetForm();
    onComplete();
    onOpenChange(false);
  };

  const handleSkip = async () => {
    if (!pendingReflection) return;
    
    // Mark as completed without data
    await supabase
      .from("check_ins")
      .update({ reflection_completed: true })
      .eq("id", pendingReflection.id);
    
    resetForm();
    onComplete();
    onOpenChange(false);
  };

  if (!pendingReflection) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto">
        <DialogHeader className="text-center space-y-2 pb-2">
          <div className="flex justify-center">
            <Mascot mood="happy" size="md" />
          </div>
          <div className="space-y-1">
            <DialogTitle className="font-display text-xl">
              How'd the work go?
            </DialogTitle>
            <p className="text-xs text-primary/80">
              Build your personal guide to the best work spots.
            </p>
          </div>
          <DialogDescription className="text-sm">
            You checked in at <span className="font-medium text-foreground">{pendingReflection.cafe_name}</span> on {format(new Date(pendingReflection.checked_in_at), "MMM d 'at' h:mm a")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Focus Rating - Primary question */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">How focused were you?</Label>
            <FocusMascotScale value={focusRating} onChange={setFocusRating} />
          </div>

          {/* WiFi Quality */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">How was the WiFi?</Label>
            <WifiQualityScale value={wifiQuality} onChange={setWifiQuality} />
          </div>

          {/* Outlets */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Plug className="h-4 w-4" />
              Outlets available?
            </Label>
            <div className="flex gap-2">
              {outletOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setOutletsAvailable(option.value)}
                  className={cn(
                    "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all",
                    "border-2",
                    outletsAvailable === option.value
                      ? "border-primary bg-primary/10"
                      : "border-border bg-secondary/30 hover:bg-secondary/50"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Crowdedness */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              How crowded was it?
            </Label>
            <div className="flex gap-1">
              {crowdednessOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setCrowdedness(option.value)}
                  className={cn(
                    "flex-1 py-2 px-1 rounded-lg text-[11px] font-medium transition-all",
                    "border-2",
                    crowdedness === option.value
                      ? "border-primary bg-primary/10"
                      : "border-transparent bg-secondary/30 hover:bg-secondary/50"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Noise Level */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Volume2 className="h-4 w-4" />
              Noise level?
            </Label>
            <div className="flex gap-2">
              {noiseOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setNoiseLevel(option.value)}
                  className={cn(
                    "flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all",
                    "border-2",
                    noiseLevel === option.value
                      ? "border-primary bg-primary/10"
                      : "border-transparent bg-secondary/30 hover:bg-secondary/50"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Would Work Again */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Would you work here again?</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setWouldWorkAgain(true)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-all",
                  "border-2",
                  wouldWorkAgain === true
                    ? "border-primary bg-primary/10"
                    : "border-border bg-secondary/30 hover:bg-secondary/50"
                )}
              >
                <ThumbsUp className={cn(
                  "h-5 w-5",
                  wouldWorkAgain === true ? "text-primary" : "text-muted-foreground"
                )} />
                <span className="font-medium">Yes</span>
              </button>
              <button
                type="button"
                onClick={() => setWouldWorkAgain(false)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-all",
                  "border-2",
                  wouldWorkAgain === false
                    ? "border-destructive bg-destructive/10"
                    : "border-border bg-secondary/30 hover:bg-secondary/50"
                )}
              >
                <ThumbsDown className={cn(
                  "h-5 w-5",
                  wouldWorkAgain === false ? "text-destructive" : "text-muted-foreground"
                )} />
                <span className="font-medium">No</span>
              </button>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Quick notes (optional)
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any tips for other remote workers?"
              className="resize-none h-20"
              maxLength={500}
              onFocus={(e) => {
                const el = e.currentTarget;
                setTimeout(() => {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 350);
              }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="ghost"
            onClick={handleSkip}
            disabled={isSubmitting}
            className="flex-1"
          >
            Skip
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
